import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'antd';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  WatercraftHelmet,
  WatercraftSpinner,
  WatercraftWordmark,
} from '../../features/watercraft';
import {
  useCreateCheckoutSessionMutation,
  useGetPricesQuery,
  useGetSubscriptionStatusQuery,
} from '../../features/watercraft/watercraftSlice';
import { useIsLoggedInQuery } from '../../features/users/usersSlice';
import { formatAsPounds } from '../../utils/payment-utils';
import { notification } from '../../utils/messaging';
import { WATERCRAFT_ROUTE } from '../../app/appConstants';
import {
  WATERCRAFT_LOGIN_ROUTE,
  WATERCRAFT_MANAGE_ROUTE,
  WATERCRAFT_PASSES_ROUTE,
  WATERCRAFT_SUCCESS_ROUTE,
} from './constants';

// Whether to request a free trial at checkout. The trial is ultimately server-
// decided, but this lets us stop offering trials via config
// (REACT_APP_WATERCRAFT_REQUEST_TRIAL=false) without a code change. Defaults to
// true when unset.
const REQUEST_TRIAL =
  process.env.REACT_APP_WATERCRAFT_REQUEST_TRIAL !== 'false';

export default function WatercraftSubscribePage(): React.JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Refetch auth on mount: after a client-side login/signup the cached getUser
  // can still read as logged-out (an errored entry carries no tag for the auth
  // mutations to invalidate), which would silently skip the auto-trigger.
  const { data: isLoggedIn, isFetching: loadingLogin } = useIsLoggedInQuery({
    refetchOnMountOrArgChange: true,
  });
  const { data: prices, isFetching: loadingPrices } = useGetPricesQuery();
  const { data: subscription, isFetching: loadingStatus } =
    useGetSubscriptionStatusQuery(undefined, {
      skip: !isLoggedIn,
      refetchOnMountOrArgChange: true,
    });
  const [createCheckoutSession, { isLoading: creatingCheckout }] =
    useCreateCheckoutSessionMutation();

  // Set when a guest is bounced through login/create-account so we can resume the
  // checkout automatically on return instead of making them click the CTA again.
  const [autoMode, setAutoMode] = useState(
    () => searchParams.get('autoTrigger') === 'true'
  );
  const autoTriggered = useRef(false);

  const price = prices?.prices?.[0];
  const trialDays = prices?.trial_days ?? 0;
  const offersTrial = REQUEST_TRIAL && trialDays > 0;

  const startCheckout = useCallback(() => {
    if (!price) {
      return;
    }
    const origin = `${window.location.protocol}//${window.location.host}`;
    createCheckoutSession({
      price_id: price.id,
      success_url: `${origin}${WATERCRAFT_SUCCESS_ROUTE}?session_id={CHECKOUT_SESSION_ID}`,
      // Clean route (no autoTrigger) so a cancelled checkout returns to the card
      // rather than immediately re-triggering the checkout in a loop.
      cancel_url: `${origin}${WATERCRAFT_ROUTE}`,
      request_trial: REQUEST_TRIAL,
    })
      .unwrap()
      .then((redirectUrl) => {
        if (redirectUrl) {
          window.location.href = redirectUrl;
        }
      })
      .catch((err) => {
        if (err?.status === 400) {
          navigate(WATERCRAFT_MANAGE_ROUTE);
          return;
        }
        setAutoMode(false);
        notification.error({
          description: 'Unable to start checkout. Please try again.',
        });
      });
  }, [price, createCheckoutSession, navigate]);

  const handleSubscribe = () => {
    if (!isLoggedIn) {
      const returnTo = `${WATERCRAFT_ROUTE}?autoTrigger=true`;
      navigate(
        `${WATERCRAFT_LOGIN_ROUTE}?redirect=${encodeURIComponent(returnTo)}`
      );
      return;
    }
    startCheckout();
  };

  useEffect(() => {
    if (
      !autoMode ||
      !isLoggedIn ||
      loadingPrices ||
      loadingStatus ||
      subscription?.has_access ||
      autoTriggered.current
    ) {
      return;
    }
    if (!price) {
      // Nothing to purchase — drop back to the card rather than spin forever.
      setAutoMode(false);
      return;
    }
    autoTriggered.current = true;
    startCheckout();
  }, [
    autoMode,
    isLoggedIn,
    loadingPrices,
    loadingStatus,
    subscription,
    price,
    startCheckout,
  ]);

  if (loadingLogin || loadingPrices || (isLoggedIn && loadingStatus)) {
    return (
      <div className='grid min-h-screen w-full place-content-center bg-watercraft-bg text-watercraft-text-primary'>
        <WatercraftHelmet />
        <WatercraftSpinner />
      </div>
    );
  }

  if (subscription?.has_access) {
    return <Navigate to={WATERCRAFT_MANAGE_ROUTE} replace />;
  }

  const autoStarting = autoMode && Boolean(isLoggedIn) && Boolean(price);
  if (autoStarting) {
    return (
      <div className='grid min-h-screen w-full place-content-center gap-4 bg-watercraft-bg text-center text-watercraft-text-primary'>
        <WatercraftHelmet />
        <WatercraftSpinner />
        <p className='text-sm text-watercraft-text-secondary'>
          {offersTrial ? 'Starting your free trial…' : 'Starting checkout…'}
        </p>
      </div>
    );
  }

  return (
    <main className='flex min-h-screen w-full flex-col items-center justify-center gap-8 bg-gradient-to-b from-watercraft-bg to-watercraft-surface p-6 text-watercraft-text-primary'>
      <WatercraftHelmet />
      <WatercraftWordmark className='h-9 w-auto text-watercraft-text-primary' />
      <section className='w-full max-w-sm rounded-xl border border-watercraft-border bg-watercraft-surface p-6 text-center'>
        {offersTrial && (
          <p className='mb-1 text-sm font-semibold text-watercraft-accent'>
            {trialDays}-day free trial
          </p>
        )}
        <h1 className='text-2xl font-bold'>Watercraft Pro Subscription</h1>
        {price ? (
          <div className='mt-4 flex items-baseline justify-center gap-2'>
            {price.cost < price.original_cost && (
              <del className='text-base text-watercraft-text-muted'>
                {formatAsPounds(price.original_cost)}
              </del>
            )}
            <span className='text-3xl font-bold'>
              {formatAsPounds(price.cost)}
            </span>
            {price.interval && (
              <span className='text-sm text-watercraft-text-secondary'>
                per {price.interval}
              </span>
            )}
          </div>
        ) : (
          <p className='mt-4 text-sm text-watercraft-text-secondary'>
            Pricing is currently unavailable.
          </p>
        )}
        <Button
          type='primary'
          className='mt-6 h-11 w-full rounded-lg border-none bg-watercraft-primary text-base font-semibold text-watercraft-text-primary hover:bg-watercraft-primary-light'
          disabled={!price || creatingCheckout}
          onClick={handleSubscribe}
        >
          {creatingCheckout ? (
            <WatercraftSpinner className='h-5 w-5 border-watercraft-text-primary/30 border-t-watercraft-text-primary' />
          ) : offersTrial ? (
            'Start free trial'
          ) : (
            'Subscribe'
          )}
        </Button>
        {price && offersTrial && (
          <p className='mt-3 text-xs text-watercraft-text-muted'>
            Free for {trialDays} days, then {formatAsPounds(price.cost)}
            {price.interval ? ` per ${price.interval}` : ''}.
          </p>
        )}
      </section>
      <p className='text-sm text-watercraft-text-secondary'>
        Not ready to subscribe?{' '}
        <Link
          to={WATERCRAFT_PASSES_ROUTE}
          className='font-semibold text-watercraft-accent underline'
        >
          Buy single-use session passes
        </Link>
      </p>
    </main>
  );
}
